// The public card page (/ryan-wold): flip, check the receipt, check a held copy.
// Reads its facts from <script id="ledger" type="application/json">.
import { WOC_TX, checkBapSigner, findReceipt, sha256Hex } from './receipt.js';

const ledger = JSON.parse(document.getElementById('ledger').textContent);
const card = document.getElementById('card');
const flipBtn = document.getElementById('flip');
const verdict = document.getElementById('verdict');
const verifyBtn = document.getElementById('verify');
const short = (s) => `${s.slice(0, 8)}…${s.slice(-4)}`;

const say = (el, state, text) => {
  el.dataset.state = state;
  el.textContent = text;
};

async function check() {
  say(verdict, 'idle', 'Checking…');

  let digest;
  try {
    const res = await fetch(ledger.vcf, { cache: 'no-store' });
    if (!res.ok) throw new Error('fetch');
    digest = await sha256Hex(await res.arrayBuffer());
  } catch (e) {
    say(verdict, 'warn', e.message === 'no-subtle' ? "This browser can't hash here. Use the curl recipe." : "Couldn't fetch the card to hash it.");
    return;
  }
  if (digest !== ledger.sha256) return say(verdict, 'bad', "Mismatch: the served card doesn't match its record.");
  if (!ledger.txid) return say(verdict, 'warn', 'Bytes match my record. No ledger receipt yet.');

  let tx;
  try {
    const res = await fetch(WOC_TX + ledger.txid);
    if (res.status === 404) return say(verdict, 'bad', 'Receipt tx not found on the ledger.');
    if (!res.ok) throw new Error('woc');
    tx = await res.json();
  } catch {
    return say(verdict, 'warn', "Bytes match my record, but I couldn't reach the ledger. Try the tx link.");
  }

  const receipt = findReceipt(tx, digest);
  if (!receipt) return say(verdict, 'bad', "The tx exists but doesn't carry this card's fingerprint.");
  if (!receipt.signer) return say(verdict, 'bad', 'The receipt is unsigned.');
  if (!receipt.signer.valid) return say(verdict, 'bad', "The receipt's signature doesn't verify.");

  const where = tx.blockheight ? `block ${tx.blockheight}` : 'the mempool (unconfirmed)';
  if (!ledger.bap_id) return say(verdict, 'warn', `Fingerprint in ${where}, signed by ${short(receipt.signer.address)}. The card names no identity to check it against.`);

  let signer;
  try {
    signer = await checkBapSigner({ address: receipt.signer.address, block: tx.blockheight, bapId: ledger.bap_id });
  } catch {
    return say(verdict, 'warn', `Fingerprint in ${where}, validly signed by ${short(receipt.signer.address)}. Couldn't reach the BAP indexer to confirm that key belongs to ${short(ledger.bap_id)}.`);
  }
  if (!signer.ok) return say(verdict, 'bad', `Signed, but ${signer.reason}.`);
  say(verdict, 'ok', `✓ Checked: fingerprint in ${where}, signed by identity ${short(ledger.bap_id)}.`);
}

flipBtn.addEventListener('click', () => {
  const on = card.classList.toggle('is-flipped');
  flipBtn.setAttribute('aria-pressed', String(on));
  flipBtn.textContent = on ? 'Show the front' : 'Flip for receipt';
  if (on && verdict.dataset.state === 'idle') check();
});

verifyBtn.disabled = false;
verifyBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  check();
});

const holderVerdict = document.getElementById('holder-verdict');
const holderFile = document.getElementById('holder-file');
holderFile.disabled = false;
holderFile.addEventListener('change', async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  try {
    const digest = await sha256Hex(await file.arrayBuffer());
    const match = ledger.versions.find((v) => v.sha256 === digest);
    if (!match) {
      say(holderVerdict, 'bad', `No issued version has this fingerprint (${digest.slice(0, 12)}…). It was changed, or it isn't mine.`);
      return;
    }
    const latest = match.rev === ledger.versions[ledger.versions.length - 1].rev;
    say(
      holderVerdict,
      'ok',
      `✓ Your copy is version ${match.rev}${latest ? ', the current one' : ', an earlier one'}. ${match.txid ? 'It has a ledger receipt.' : 'Its receipt is pending.'}`,
    );
  } catch {
    say(holderVerdict, 'warn', "This browser can't hash files here.");
  }
});
